import { CircularProgress, MenuItem, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { diagnosticsApi, type DxNamedMasterDto } from "@/api/diagnostics";

/** Which diagnostics catalog master to offer. */
export type DxMasterKind = "specimen" | "container";

const LOADERS: Record<DxMasterKind, () => Promise<DxNamedMasterDto[]>> = {
  specimen: () => diagnosticsApi.listSpecimens(),
  container: () => diagnosticsApi.listContainers(),
};

const LABELS: Record<DxMasterKind, string> = {
  specimen: "Specimen",
  container: "Container",
};

/**
 * Dropdown over a small diagnostics catalog master (specimen type, container type). These lists run
 * to a couple of dozen entries, so a select showing all of them beats a search box — and it
 * replaces a raw GUID field that could only be filled by copying an id from elsewhere.
 *
 * Inactive entries are hidden, except one already selected, so an existing record still shows what
 * it referenced.
 */
export function DxMasterPicker({
  kind, value, onChange, label, required, disabled, sx,
}: {
  kind: DxMasterKind;
  value: string;
  onChange: (id: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  sx?: object;
}) {
  const q = useQuery({
    queryKey: ["dx-master-picker", kind],
    queryFn: LOADERS[kind],
    staleTime: 5 * 60_000,
  });

  const options = (q.data ?? []).filter(o => o.isActive || o.id === value);
  const text = label ?? LABELS[kind];

  return (
    <TextField
      select size="small" sx={sx} disabled={disabled || q.isLoading}
      label={`${text}${required ? " *" : ""}`}
      value={options.some(o => o.id === value) ? value : ""}
      onChange={(e) => onChange(e.target.value)}
      helperText={q.isError ? `Could not load ${text.toLowerCase()}s` : undefined}
      InputProps={q.isLoading ? { endAdornment: <CircularProgress size={16} /> } : undefined}
    >
      <MenuItem value=""><em>— pick {text.toLowerCase()} —</em></MenuItem>
      {options.map(o => (
        <MenuItem key={o.id} value={o.id}>
          {o.code} — {o.name}{o.isActive ? "" : " (inactive)"}
        </MenuItem>
      ))}
    </TextField>
  );
}
